import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { productCodesQueries } from '../utils/queries';
import { setMinimalPromiseTime } from 'src/common/utils/setMinimalPromiseTime';
import { fetchProductCodes } from '../api/productCodes';
import { ProductCode } from '../models/productCodes';

const PRODUCT_CODES_PAGE_SIZE = 100;

export const useInfinityProductCodesQuery = (
  searchText: string,
  options?: { enabled?: boolean; minTime?: number },
) => {
  const enabled = options?.enabled ?? true;
  const minTime = options?.minTime ?? 0;

  const { data, ...queryProps } = useInfiniteQuery({
    queryKey: productCodesQueries.infinityList({
      search: searchText,
      pageSize: PRODUCT_CODES_PAGE_SIZE,
    }),
    queryFn: async ({ pageParam }) => {
      if (minTime > 0) {
        return setMinimalPromiseTime(
          fetchProductCodes({
            search: searchText,
            pageSize: PRODUCT_CODES_PAGE_SIZE,
            pageNumber: pageParam,
          }),
          minTime,
        );
      }

      return fetchProductCodes({
        search: searchText,
        pageSize: PRODUCT_CODES_PAGE_SIZE,
        pageNumber: pageParam,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length >= PRODUCT_CODES_PAGE_SIZE) {
        return pages.length + 1;
      }
    },

    enabled,
  });

  const productCodes = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages.flat().map((code: ProductCode) => code.productCode);
  }, [data]);

  return {
    productCodes,
    ...queryProps,
  };
};
